import { MouseEvent, useState } from 'react';

import find from 'lodash-es/find';
import groupBy from 'lodash-es/groupBy';
import { useConfirm } from 'material-ui-confirm';
import dayjs from 'src/helpers/dayjs';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import useEffectOnce from 'react-use/lib/useEffectOnce';

import PageBox from 'src/common/components/PageBox';
import { ticketCardGridContainerPaddingSx } from 'src/common/styles/ticketCardStyles';
import { ICompetition } from 'src/interfaces/Competition';
import { ECheckinTimerType, IBulkCheckinTimer, ICheckin } from 'src/interfaces/Checkin';
import usePersistentView from 'src/hooks/usePersistentView';
import { IFormCheckin } from 'src/interfaces/Checkin';
import CheckinModal from 'src/common/modals/CheckinModal';
import CheckinSerialModal from 'src/common/modals/CheckinSerialModal';
import { isSignupOpenForEdition } from 'src/helpers/visibilityFilter';
import useCheckinsStore from 'src/stores/checkinsStore';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';
import { BulkActionOptions } from './components/CheckinBulkActionsModal';
import CheckinDistance from './components/CheckinDistance';

interface Props {
  competition: ICompetition;
  distances: string[];
  editionId: string;
}

const Competition = ({ competition, distances, editionId }: Props) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { enqueueSnackbar } = useSnackbar();

  const { activeEditionId, distancesById, editionsById } = useSystemOptionsStore(
    systemOptionsState => systemOptionsState,
  );
  const isCurrentEdition = Boolean(activeEditionId && editionId === activeEditionId);
  const edition = editionsById?.[editionId];
  const isSignupOpen = isSignupOpenForEdition(edition);
  const canResetSerialNumbers = isCurrentEdition && isSignupOpen;

  const {
    checkins,
    deleteCheckin: doDeleteCheckin,
    fetchCheckinsByCompetitionId: doFetchCheckinsByCompetitionId,
    handleTimer: doHandleTimer,
    handleBulkTimer: doHandleBulkTimer,
    isLoadingCheckins,
    resetCheckins: doResetCheckins,
    resetCounter: doResetCounter,
    saveCheckin: doSaveCheckin,
    setDnf: doSetDnf,
  } = useCheckinsStore(checkinsState => checkinsState);

  const [newCheckin, setNewCheckin] = useState<ICheckin | undefined>();
  const [checkinModalOpen, setCheckinModalOpen] = useState(false);
  const [selectedCheckin, setSelectedCheckin] = useState<ICheckin | undefined>(undefined);

  const [view, setView] = usePersistentView();

  useEffectOnce(() => {
    return () => {
      doResetCheckins();
    };
  });

  const handleDelete = async (id: string, e?: MouseEvent<unknown>) => {
    e && e.stopPropagation();
    const { confirmed } = await confirm({
      title: t('checkins.deleteConfirm'),
      description: t('checkins.deleteDescription'),
      confirmationText: t('confirmProvider.confirmationText'),
      cancellationText: t('confirmProvider.cancellationText'),
    });
    if (!confirmed) {
      enqueueSnackbar(t('checkins.deleteErrorMessage'), {
        variant: 'error',
      });
      return;
    }
    await doDeleteCheckin(id)
      .then(() => {
        enqueueSnackbar(t('checkins.deleteSuccessfulMessage'), {
          variant: 'success',
        });
      })
      .catch(
        ({
          response: {
            data: { errorCode, message },
          },
        }) => {
          enqueueSnackbar(errorCode ? t(`checkins.errors.${errorCode}`) : message, {
            variant: 'error',
          });
        },
      );
  };

  const handleOpenCheckinModal = (id?: string, e?: MouseEvent<unknown>) => {
    e && e.stopPropagation();
    const checkin = find(checkins, { id });
    setSelectedCheckin(checkin);
    setCheckinModalOpen(true);
  };

  const handleResetCounter = async (dist: string, e?: MouseEvent<unknown>) => {
    e && e.stopPropagation();
    if (!canResetSerialNumbers) {
      enqueueSnackbar(
        !isCurrentEdition
          ? t('timers.bulkActionsDisabledNotCurrentEdition')
          : t('counters.resetDisabledSignupsClosed'),
        { variant: 'error' },
      );
      return;
    }
    const { confirmed } = await confirm({
      title: t('counters.resetAllCounters'),
      description: t('counters.confirmResetCounters'),
      confirmationText: t('confirmProvider.confirmationText'),
      cancellationText: t('confirmProvider.cancellationText'),
    });
    if (!confirmed) {
      enqueueSnackbar(t('counters.resetErrorMessage'), {
        variant: 'error',
      });
      return;
    }
    try {
      await doResetCounter({
        competitionId: competition.id,
        distanceId: dist,
        editionId,
      });
      enqueueSnackbar(t('counters.resetSuccessfulMessage'), {
        variant: 'success',
      });
    } catch {
      enqueueSnackbar(t('counters.resetNotAllowedMessage'), {
        variant: 'error',
      });
    }
  };

  const handleTimer = async (
    id: string,
    type: ECheckinTimerType,
    timeStamp: Date = dayjs().toDate(),
  ) => {
    const selectedCheckin = find(checkins, { id });

    if (!selectedCheckin) return;

    const { startTime, finishTime } = selectedCheckin;

    let confirmTitle: string = '';
    let confirmationText: string = '';
    let snackbarSuccess: string = '';
    let snackbarError: string = '';

    switch (type) {
      case ECheckinTimerType.start:
        confirmTitle = !startTime ? t('timers.startStartTitle') : t('timers.updateStartTitle');
        confirmationText = !startTime
          ? t('timers.startStartConfirm')
          : t('timers.updateStartConfirm');
        snackbarSuccess = !startTime
          ? t('timers.startStartSuccess')
          : t('timers.updateStartSuccess');
        snackbarError = !startTime ? t('timers.startStartError') : t('timers.updateStartError');
        break;
      case ECheckinTimerType.finish:
        confirmTitle = !finishTime ? t('timers.startFinishTitle') : t('timers.updateFinishTitle');
        confirmationText = !finishTime
          ? t('timers.startFinishConfirm')
          : t('timers.updateFinishConfirm');
        snackbarSuccess = !finishTime
          ? t('timers.startFinishSuccess')
          : t('timers.updateFinishSuccess');
        snackbarError = !finishTime ? t('timers.startFinishError') : t('timers.updateFinishError');
        break;
      case ECheckinTimerType.clear:
        confirmTitle = t('timers.clearTitle');
        confirmationText = t('timers.clearConfirm');
        snackbarSuccess = t('timers.clearSuccess');
        snackbarError = t('timers.clearError');
        break;

      default:
        break;
    }

    const { confirmed } = await confirm({
      title: confirmTitle,
      confirmationText,
      cancellationText: t('timers.cancel'),
    });
    if (!confirmed) {
      enqueueSnackbar(snackbarError, {
        variant: 'error',
      });
      return;
    }
    await doHandleTimer({
      id: selectedCheckin.id,
      timeStamp,
      type,
    });
    enqueueSnackbar(snackbarSuccess, {
      variant: 'success',
    });
  };

  const handleDnf = async (id: string, dnf: boolean) => {
    const selectedCheckin = find(checkins, { id });
    if (!selectedCheckin) return;

    const { confirmed } = await confirm({
      title: dnf ? t('timers.setDnfTitle') : t('timers.clearDnfTitle'),
      confirmationText: dnf ? t('timers.setDnfConfirm') : t('timers.clearDnfConfirm'),
      cancellationText: t('timers.cancel'),
    });
    if (!confirmed) {
      enqueueSnackbar(dnf ? t('timers.setDnfError') : t('timers.clearDnfError'), {
        variant: 'error',
      });
      return;
    }
    await doSetDnf({ id: selectedCheckin.id, dnf });
    enqueueSnackbar(dnf ? t('timers.setDnfSuccess') : t('timers.clearDnfSuccess'), {
      variant: 'success',
    });
  };

  const handleBulkTimer = async (
    payload: Omit<IBulkCheckinTimer, 'editionId'>,
    options?: BulkActionOptions,
  ) => {
    let confirmTitle: string = '';
    let confirmationText: string = '';
    let snackbarSuccess: string = '';
    let snackbarError: string = '';

    switch (payload.type) {
      case ECheckinTimerType.start:
        confirmTitle = t('timers.bulkStartTitle');
        confirmationText = t('timers.bulkStartConfirm');
        snackbarSuccess = t('timers.bulkStartSuccess');
        snackbarError = t('timers.bulkStartError');
        break;
      case ECheckinTimerType.finish:
        confirmTitle = t('timers.bulkFinishTitle');
        confirmationText = t('timers.bulkFinishConfirm');
        snackbarSuccess = t('timers.bulkFinishSuccess');
        snackbarError = t('timers.bulkFinishError');
        break;
      case ECheckinTimerType.clear:
        confirmTitle = t('timers.bulkClearTitle');
        confirmationText = t('timers.bulkClearConfirm');
        snackbarSuccess = t('timers.bulkClearSuccess');
        snackbarError = t('timers.bulkClearError');
        break;

      default:
        break;
    }

    if (!options?.skipConfirm) {
      const { confirmed } = await confirm({
        title: confirmTitle,
        confirmationText,
        cancellationText: t('timers.cancel'),
      });
      if (!confirmed) {
        enqueueSnackbar(snackbarError, {
          variant: 'error',
        });
        return;
      }
    }
    await doHandleBulkTimer({
      ...payload,
      editionId,
    });
    enqueueSnackbar(snackbarSuccess, {
      variant: 'success',
    });
  };

  const handleCloseCheckinModal = () => {
    setSelectedCheckin(undefined);
    setCheckinModalOpen(false);
  };

  const saveCheckin = async (checkin: IFormCheckin, id?: string) => {
    await doSaveCheckin(checkin, id)
      .then(newCheckin => {
        doFetchCheckinsByCompetitionId(editionId, competition.id);
        enqueueSnackbar(t('checkins.saveCheckinSuccess'), {
          variant: 'success',
        });
        setNewCheckin(newCheckin || undefined);
      })
      .catch(
        ({
          response: {
            data: { errorCode, message },
          },
        }) => {
          enqueueSnackbar(errorCode ? t(`checkins.errors.${errorCode}`) : message, {
            variant: 'error',
          });
        },
      )
      .finally(() => handleCloseCheckinModal());
  };

  if (!distancesById) return null;

  const checkinsByDistance = groupBy(checkins, 'distance');

  return (
    <>
      <PageBox sx={ticketCardGridContainerPaddingSx} isLoading={isLoadingCheckins}>
        {distances.map(distanceId => (
          <CheckinDistance
            key={distanceId}
            checkins={checkinsByDistance[distanceId] ?? []}
            competitionId={competition.id}
            distanceId={distanceId}
            onClickBulkTimer={handleBulkTimer}
            onClickTimer={handleTimer}
            onSetDnf={handleDnf}
            onDeleteCheckin={handleDelete}
            onEditCheckin={handleOpenCheckinModal}
            onResetCounter={handleResetCounter}
            onSetView={setView}
            view={view}
            isCurrentEdition={isCurrentEdition}
            isSignupOpen={isSignupOpen}
          />
        ))}
      </PageBox>

      <CheckinSerialModal
        open={!!newCheckin}
        handleClose={() => {
          setNewCheckin(undefined);
        }}
        serialNo={newCheckin?.ser}
      />

      {checkinModalOpen && !!selectedCheckin && (
        <CheckinModal
          id="checkin"
          isLoading={isLoadingCheckins}
          open={checkinModalOpen}
          handleClose={handleCloseCheckinModal}
          handleSave={saveCheckin}
          selectedCheckin={selectedCheckin}
        />
      )}
    </>
  );
};

export default Competition;
